'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import type { MarketplacePromo } from '@/lib/marketplace/queries'

export function PromoBanner({ promo }: { promo: MarketplacePromo }) {
  const router = useRouter()
  const [hidden, setHidden] = useState(false)
  const [busy, setBusy] = useState(false)

  if (hidden) return null

  const dismiss = async () => {
    if (busy) return
    setBusy(true)
    setHidden(true)
    try {
      await fetch('/api/marketplace/promos/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promo_id: promo.id }),
      })
      router.refresh()
    } catch {
      // Best-effort dismissal; local state already hides the banner.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative flex flex-col gap-2 rounded-xl border border-atrium-border bg-accent-50 px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-atrium-black">{promo.headline}</div>
        {promo.body && (
          <p className="mt-0.5 text-[13px] leading-relaxed text-atrium-black/70">
            {promo.body}
          </p>
        )}
      </div>
      {promo.cta_label && promo.cta_url && (
        <Link
          href={promo.cta_url}
          className="inline-flex shrink-0 items-center justify-center rounded-lg bg-accent-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-accent-700"
        >
          {promo.cta_label}
        </Link>
      )}
      {promo.dismissible && (
        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          aria-label="Dismiss"
          className="absolute right-2 top-2 grid size-7 place-items-center rounded-full text-atrium-black/50 transition-colors hover:bg-atrium-content hover:text-atrium-black sm:static sm:size-8"
        >
          <HugeiconsIcon icon={Cancel01Icon} className="size-3.5" />
        </button>
      )}
    </div>
  )
}
